import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, TruckIcon, WrenchIcon, CheckIcon } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { products } from '../../data/products';
import { formatEur } from '../../lib/format';
interface ProductComparisonProps {
  /** When set, that product's column is scrolled into view and highlighted briefly. */
  focusProductId?: string;
  /** Bump this to re-trigger the highlight even when focusProductId is unchanged. */
  focusNonce?: number;
}
export function ProductComparison({
  focusProductId,
  focusNonce
}: ProductComparisonProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const [activeId, setActiveId] = useState<string | null>(null);
  const colRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // React to an externally-focused model: scroll its column into view and
  // highlight it for ~2.6s, then return to the neutral comparison state.
  useEffect(() => {
    if (!focusProductId) return;
    setActiveId(focusProductId);
    const cell = colRefs.current[focusProductId];
    const container = scrollContainerRef.current;
    if (cell && container) {
      // Horizontal-only centering inside the scroll container, so it does NOT
      // fight the page-level vertical scroll to the table (which previously
      // stopped the page halfway). Works on mobile too.
      const target = cell.offsetLeft - container.clientWidth / 2 + cell.clientWidth / 2;
      container.scrollTo({
        left: Math.max(0, target),
        behavior: 'smooth'
      });
    }
    const timeout = setTimeout(() => setActiveId(null), 2600);
    return () => clearTimeout(timeout);
  }, [focusProductId, focusNonce]);
  const isActive = (id: string) => activeId === id;
  const isDimmed = (id: string) => activeId !== null && activeId !== id;
  // Shared cell classes for a product column based on highlight state.
  const colClass = (id: string, base: string) => {
    const hero = id === 'raptor';
    return [base, 'transition-all duration-500', isDimmed(id) ? 'opacity-40' : 'opacity-100', hero && !isDimmed(id) ? 'bg-brand-yellow/5' : '', isActive(id) ? 'bg-brand-yellow/10 shadow-[inset_0_0_0_2px_rgba(253,185,19,0.7)]' : ''].filter(Boolean).join(' ');
  };
  // Staggered reveal for the active column's value, top-to-bottom.
  const CellValue = ({
    id,
    rowIndex,
    children
  }: {
    id: string;
    rowIndex: number;
    children: React.ReactNode;
  }) => {
    if (!isActive(id)) return <>{children}</>;
    return <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.32,
      delay: rowIndex * 0.08,
      ease: 'easeOut'
    }} data-id="element-492" data-mp="3fl4flzfxtjgq">
        {children}
      </motion.div>;
  };
  return <div className="space-y-12" data-id="element-493" data-mp="2mzphuen4r0ke">
      {/* Comparison Table */}
      <div ref={scrollContainerRef} className="relative overflow-x-auto rounded-2xl border border-dark-border bg-dark-surface/80 backdrop-blur-sm shadow-xl custom-scrollbar" data-id="element-494" data-mp="2i0cl926gif4m">
        <table className="w-full text-left border-collapse min-w-[900px]" data-id="element-495" data-mp="2rve9zwwtlf6o">
          <thead data-id="element-496" data-mp="35vyt6u6ndd0p">
            <tr data-id="element-497" data-mp="35vfl056su2zv">
              <th className="sticky left-0 z-20 bg-dark-surface/95 backdrop-blur border-b border-r border-dark-border p-6 w-48 align-bottom" data-id="element-498" data-mp="35uw66xcg3uq2">
                <span className="text-xs font-bold tracking-widest text-gray-500 uppercase" data-id="element-499" data-mp="3fn1u9vzktrzz">
                  {en ? 'Specifications' : ru ? 'Характеристики' : 'Specifikacije'}
                </span>
              </th>

              {products.map(product => {
              const hero = product.id === 'raptor';
              return <th key={product.id} ref={el => {
                colRefs.current[product.id] = el;
              }} className={colClass(product.id, 'relative p-6 border-b border-dark-border min-w-[220px] align-top')} data-id="element-500" data-mp="1hxwlpdeek7hv">
                    {hero && <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-brand-yellow text-dark-bg text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-b-md whitespace-nowrap" data-id="element-501" data-mp="z1oh8bt7s0nm">
                        {en ? 'Recommended' : ru ? 'Рекомендуем' : 'Preporuka'}
                      </div>}

                    <div className="text-center mt-4" data-id="element-502" data-mp="1cxige346rbnj">
                      <motion.h3 animate={isActive(product.id) ? {
                    scale: [1, 1.06, 1, 1.06, 1]
                  } : {
                    scale: 1
                  }} transition={{
                    duration: 0.8,
                    ease: 'easeInOut'
                  }} className={`text-xl font-black tracking-tight ${hero ? 'text-brand-yellow' : 'text-white'}`} data-id="element-503" data-mp="2jf5iq54d83ar">
                        {product.name}
                      </motion.h3>
                      <p className={`text-xs uppercase tracking-widest mt-1 font-bold ${hero ? 'text-brand-yellow/80' : 'text-brand-yellow/70'}`} data-id="element-504" data-mp="2avqrfxgqn4uf">
                        {product.model}
                      </p>

                      <div className="mt-4" data-id="element-505" data-mp="41mznm2pjsri">
                        {product.priceLabel ? <span className="bg-gradient-to-r from-brand-yellow via-yellow-200 to-brand-yellow text-dark-bg animate-shine font-black text-xs tracking-widest uppercase px-3 py-1 rounded-full shadow-[0_0_15px_rgba(253,185,19,0.4)] whitespace-nowrap border border-yellow-300 inline-block" data-id="element-506" data-mp="2e369ga9gs9">
                            {lang === 'en' && product.priceLabelEn ? product.priceLabelEn : lang === 'ru' && product.priceLabelRu ? product.priceLabelRu : product.priceLabel}
                          </span> : <div data-id="element-507" data-mp="23a519ue7n1tw">
                            {product.originalPrice && <p className="text-sm text-gray-500 line-through mb-0.5" data-id="element-508" data-mp="288y32pk4x4e8">
                                €{formatEur(product.originalPrice)}
                              </p>}
                            <p className={`text-2xl font-black ${hero ? 'text-brand-yellow' : 'text-white'}`} data-id="element-509" data-mp="wuc8kajuqfvg">
                              €{formatEur(product.price)}
                            </p>
                            <p className="text-[10px] text-gray-500 mt-0.5 uppercase tracking-wider" data-id="element-510" data-mp="kd9s7m27xj52">
                              {en ? 'excl. VAT' : ru ? 'без НДС' : 'bez PDV'}
                            </p>
                          </div>}
                      </div>
                    </div>
                  </th>;
            })}
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-border" data-id="element-511" data-mp="ld8giesj0th">
            {/* Capacity */}
            <tr data-id="element-512" data-mp="fe6xovjv6vbc">
              <td className="sticky left-0 z-10 bg-dark-surface/95 backdrop-blur border-r border-dark-border p-4 text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-513" data-mp="aldtc8sb5ozs">
                {en ? 'Capacity' : ru ? 'Грузоподъёмность' : 'Nosivost'}
              </td>
              {products.map(product => <td key={product.id} className={colClass(product.id, 'p-4 text-sm font-semibold text-white text-center')} data-id="element-514" data-mp="2sj185h6mog37">
                  <CellValue id={product.id} rowIndex={0} data-id="element-515" data-mp="32g5xfpvrsro8">
                    {product.specs.capacity}
                  </CellValue>
                </td>)}
            </tr>

            {/* Motor */}
            <tr data-id="element-516" data-mp="3bh430clux78x">
              <td className="sticky left-0 z-10 bg-dark-surface/95 backdrop-blur border-r border-dark-border p-4 text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-517" data-mp="3bfklkiogsq5k">
                {en ? 'Motor' : ru ? 'Двигатель' : 'Motor'}
              </td>
              {products.map(product => <td key={product.id} className={colClass(product.id, 'p-4 text-sm font-semibold text-white text-center')} data-id="element-518" data-mp="3q8mfw1jf6lo2">
                  <CellValue id={product.id} rowIndex={1} data-id="element-519" data-mp="1nke3u2xwz303">
                    {product.specs.motor}
                  </CellValue>
                </td>)}
            </tr>

            {/* Battery */}
            <tr data-id="element-520" data-mp="1eiemtamwfyx5">
              <td className="sticky left-0 z-10 bg-dark-surface/95 backdrop-blur border-r border-dark-border p-4 text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-521" data-mp="1ejzfsvtkf9sw">
                {en ? 'Battery' : ru ? 'Аккумулятор' : 'Baterija'}
              </td>
              {products.map(product => <td key={product.id} className={colClass(product.id, 'p-4 text-sm font-semibold text-white text-center')} data-id="element-522" data-mp="22a3j122ud7jd">
                  <CellValue id={product.id} rowIndex={2} data-id="element-523" data-mp="1sh4t9gvlzay6">
                    {lang === 'en' && product.specs.batteryEn ? product.specs.batteryEn : lang === 'ru' && product.specs.batteryRu ? product.specs.batteryRu : product.specs.battery}
                  </CellValue>
                </td>)}
            </tr>

            {/* Weight */}
            <tr data-id="element-524" data-mp="3egx5rhdodth1">
              <td className="sticky left-0 z-10 bg-dark-surface/95 backdrop-blur border-r border-dark-border p-4 text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-525" data-mp="3ehf4cknt8js4">
                {en ? 'Weight' : ru ? 'Вес' : 'Težina'}
              </td>
              {products.map(product => <td key={product.id} className={colClass(product.id, 'p-4 text-sm font-semibold text-white text-center')} data-id="element-526" data-mp="3ihh4gpdwd55a">
                  <CellValue id={product.id} rowIndex={3} data-id="element-527" data-mp="3saefkagk7rtv">
                    {product.specs.weight}
                  </CellValue>
                </td>)}
            </tr>

            {/* Key Features */}
            <tr data-id="element-528" data-mp="op4fjd87hmg5">
              <td className="sticky left-0 z-10 bg-dark-surface/95 backdrop-blur border-r border-dark-border p-4 text-xs font-bold uppercase tracking-wider text-gray-400 align-top pt-6" data-id="element-529" data-mp="oom9xpznlvvk">
                {en ? 'Key Features' : ru ? 'Особенности' : 'Karakteristike'}
              </td>
              {products.map(product => <td key={product.id} className={colClass(product.id, 'p-4 align-top')} data-id="element-530" data-mp="km7xbnmkvscq">
                  <CellValue id={product.id} rowIndex={4} data-id="element-531" data-mp="apcwgy2u1fva">
                    <ul className="space-y-3" data-id="element-532" data-mp="aqxsks4sp6v9">
                      {(lang === 'en' && product.highlightsEn ? product.highlightsEn : lang === 'ru' && product.highlightsRu ? product.highlightsRu : product.highlights).map((highlight, idx) => <li key={idx} className="flex items-start gap-2 text-left" data-id="element-533" data-mp="3o4iynucatsdc" data-mpi={idx}>
                          <CheckIcon className="w-4 h-4 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-534" data-mp="1tpvqvia48enr" />
                          <span className="text-sm text-gray-300 leading-tight" data-id="element-535" data-mp="1ovhnotp7p38j">
                            {highlight}
                          </span>
                        </li>)}
                    </ul>
                  </CellValue>
                </td>)}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Notes */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-536" data-mp="28m37y9q88zyt">
        <div className="flex items-start gap-3 bg-dark-surface/60 border border-dark-border/40 rounded-xl px-5 py-4" data-id="element-537" data-mp="23lfhbp5wul54">
          <div className="w-8 h-8 rounded-full bg-brand-yellow/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-538" data-mp="1ymcdu95kwmz9">
            <ShieldCheckIcon className="w-4 h-4 text-brand-yellow" data-id="element-539" data-mp="s8cz6po5xniu" />
          </div>
          <div data-id="element-540" data-mp="n6dyjnjt54pl">
            <p className="text-sm text-gray-300" data-id="element-541" data-mp="na1suox2tv5d">
              {en ? <>
                  Factory warranty of{' '}
                  <span className="text-brand-yellow font-semibold" data-id="element-542" data-mp="i2lju8o9tktq">
                    one year
                  </span>{' '}
                  for all legal entities.
                </> : ru ? <>
                  Заводская гарантия{' '}
                  <span className="text-brand-yellow font-semibold" data-id="element-543" data-mp="rur7j67hpg34">
                    один год
                  </span>{' '}
                  для всех юридических лиц.
                </> : <>
                  Fabrička garancija{' '}
                  <span className="text-brand-yellow font-semibold" data-id="element-544" data-mp="15th6yshs7run">
                    godinu dana
                  </span>{' '}
                  za sva pravna lica.
                </>}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 bg-dark-surface/60 border border-dark-border/40 rounded-xl px-5 py-4" data-id="element-545" data-mp="3civwi6wtq74a">
          <div className="w-8 h-8 rounded-full bg-brand-yellow/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-546" data-mp="16u3hfxd1yegc">
            <WrenchIcon className="w-4 h-4 text-brand-yellow" data-id="element-547" data-mp="11va4q75larh8" />
          </div>
          <div data-id="element-548" data-mp="1fui718x1ufpn">
            <p className="text-sm text-gray-300" data-id="element-549" data-mp="1fv1efvp2plpf">
              {en ? 'Network of expert service technicians, original parts and operational training.' : ru ? 'Сеть квалифицированных сервисных специалистов, оригинальные запчасти и обучение работе.' : 'Mreža stručnih servisera, originalni delovi i obuka za rad.'}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 bg-dark-surface/60 border border-dark-border/40 rounded-xl px-5 py-4" data-id="element-550" data-mp="wys4nkl7fhb8">
          <div className="w-8 h-8 rounded-full bg-brand-yellow/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-551" data-mp="4gee86276xm6">
            <TruckIcon className="w-4 h-4 text-brand-yellow" data-id="element-552" data-mp="3vnh66zpv17xg" />
          </div>
          <div data-id="element-553" data-mp="2b4wwnifer8pj">
            <p className="text-sm text-gray-300" data-id="element-554" data-mp="2b4530d5dfl1w">
              {en ? <>
                  Delivery in{' '}
                  <span className="text-brand-yellow font-semibold" data-id="element-555" data-mp="2fwy4ms5jmd9r">
                    4–8 weeks
                  </span>{' '}
                  directly from production.
                </> : ru ? <>
                  Поставка за{' '}
                  <span className="text-brand-yellow font-semibold" data-id="element-556" data-mp="2use2775c0raw">
                    4–8 недель
                  </span>{' '}
                  напрямую с производства.
                </> : <>
                  Isporuka od{' '}
                  <span className="text-brand-yellow font-semibold" data-id="element-557" data-mp="2ptknxci80w9o">
                    4–8 nedelja
                  </span>{' '}
                  direktno iz proizvodnje.
                </>}
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-[11px] text-gray-600 leading-relaxed text-center max-w-2xl mx-auto" data-id="element-558" data-mp="1ecb8r264kqe6">
        {en ? 'Note: The stated capacity is designed for optimal operation and motor longevity. Constant overloading above the upper limit is not recommended.' : ru ? 'Примечание: Указанная грузоподъёмность рассчитана на оптимальную работу и долговечность мотора. Постоянная перегрузка выше верхнего предела не рекомендуется.' : 'Napomena: Navedena nosivost je projektovana za optimalan rad i dugovečnost motora. Ne preporučuje se konstantno preopterećenje preko gornje granice.'}
      </p>
    </div>;
}